import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import { formatDateKey, formatNumber } from '../../utils/statisticsFormat.js';
import {
  DetailBarChart,
  DetailLineChart,
  MiniBarChart,
  MiniLineChart,
} from './StatisticsCharts.jsx';
import './StatisticsCharts.css';
import './StatisticsDetailShared.css';

const METRIC_CONFIG = {
  licensesAdded: {
    label: 'ライセンス追加',
    description: '対象日に新規契約または試用開始されたライセンス数',
    chartType: 'bar',
  },
  licensesRemoved: {
    label: 'ライセンス減少',
    description: '対象日に解約（canceledAt）されたライセンス数',
    chartType: 'bar',
  },
  operatingExistingClinics: {
    label: '稼働医院（既存）',
    description: '既存利用がある医院のうち、対象日に何らかの Firestore 操作があった医院数',
    chartType: 'line',
  },
};

function readActivityValue(product, metricKey) {
  const activity = product?.activity || {};
  if (metricKey === 'licensesAdded') return Number(activity.licensesAdded || 0);
  if (metricKey === 'licensesRemoved') return Number(activity.licensesRemoved || 0);
  if (metricKey === 'operatingExistingClinics') return Number(activity.operatingExistingClinics || 0);
  return 0;
}

function StatisticsMetricDetailModal({
  metricKey,
  productKey,
  targetDateKey,
  onClose,
}) {
  const repo = useMemo(() => AdminRepository.getInstance(), []);
  const config = METRIC_CONFIG[metricKey];
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTimeseries = useCallback(async () => {
    if (!metricKey || !productKey) return;
    setLoading(true);
    setError('');
    try {
      const result = await repo.getStatisticsProductTimeseries(productKey, {
        metric: metricKey,
        days,
        targetDateKey,
      });
      setData(result);
    } catch (err) {
      setError(err.message || '時系列データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [repo, metricKey, productKey, targetDateKey, days]);

  useEffect(() => {
    loadTimeseries();
  }, [loadTimeseries]);

  if (!metricKey || !config) return null;

  const points = data?.points || [];
  const Chart = config.chartType === 'line' ? DetailLineChart : DetailBarChart;
  const latestPoint = points.length > 0 ? points[points.length - 1] : null;
  const totalValue = points.reduce((sum, point) => sum + Number(point.value || 0), 0);
  const maxValue = points.reduce((max, point) => Math.max(max, Number(point.value || 0)), 0);

  return (
    <div className="stats-metric-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="stats-metric-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="stats-metric-modal-title"
      >
        <div className="stats-metric-modal__header">
          <div className="stats-metric-modal__header-main">
            <p className="stats-metric-modal__eyebrow">日次アクティビティ</p>
            <h3 id="stats-metric-modal-title">{config.label}</h3>
            <p className="stats-metric-modal__description">{config.description}</p>
            <div className="stats-metric-modal__meta">
              <span className="stats-metric-modal__badge">{productKey}</span>
              {latestPoint && (
                <span className="stats-metric-modal__meta-item">
                  最新 {formatDateKey(latestPoint.targetDateKey)}: {formatNumber(latestPoint.value)}
                </span>
              )}
            </div>
          </div>
          <button type="button" className="stats-metric-modal__close" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        <div className="stats-metric-modal__controls">
          <label className="stats-metric-modal__control-label">
            <span>表示日数</span>
            <select className="admin-select stats-metric-modal__select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={14}>14日</option>
              <option value={30}>30日</option>
              <option value={60}>60日</option>
              <option value={90}>90日</option>
            </select>
          </label>
          {!loading && points.length > 0 && (
            <div className="stats-metric-modal__summary">
              <span>合計 <strong>{formatNumber(totalValue)}</strong></span>
              <span>最大 <strong>{formatNumber(maxValue)}</strong></span>
            </div>
          )}
        </div>

        <div className="stats-metric-modal__body">
          {error && <div className="stats-alert danger stats-metric-modal__message">{error}</div>}

          {loading ? (
            <div className="stats-empty stats-metric-modal__message">時系列データを読み込んでいます...</div>
          ) : (
            <>
              <div className="stats-metric-modal__chart">
                {points.length > 0 ? (
                  <Chart points={points} />
                ) : (
                  <div className="stats-empty stats-metric-modal__chart-empty">表示できるグラフデータがありません。</div>
                )}
              </div>

              <div className="stats-metric-modal__table-section">
                <h4>日次一覧</h4>
                <div className="stats-table-wrap">
                  <table className="stats-table stats-table--compact">
                    <thead>
                      <tr>
                        <th>対象日</th>
                        <th className="stats-metric-modal__value-col">値</th>
                        <th className="stats-metric-modal__status-col">データ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...points].reverse().map((point) => (
                        <tr key={point.targetDateKey}>
                          <td>{formatDateKey(point.targetDateKey)}</td>
                          <td className="stats-metric-modal__value-col">{formatNumber(point.value)}</td>
                          <td className="stats-metric-modal__status-col">
                            <span className={`stats-metric-modal__data-badge${point.hasData ? ' is-yes' : ' is-no'}`}>
                              {point.hasData ? 'あり' : 'なし'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {points.length === 0 && (
                        <tr><td colSpan="3">時系列データがありません。</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const StatisticsProductActivityCards = ({ product, productKey, targetDateKey }) => {
  const repo = useMemo(() => AdminRepository.getInstance(), []);
  const [seriesByMetric, setSeriesByMetric] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(
      Object.keys(METRIC_CONFIG).map(async (metricKey) => {
        const result = await repo.getStatisticsProductTimeseries(productKey, {
          metric: metricKey,
          days: 14,
          targetDateKey,
        });
        return [metricKey, result?.points || []];
      })
    ).then((entries) => {
      if (cancelled) return;
      setSeriesByMetric(Object.fromEntries(entries));
      setLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setSeriesByMetric({});
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [repo, productKey, targetDateKey]);

  return (
    <>
      <div className="stats-activity-cards">
        {Object.entries(METRIC_CONFIG).map(([metricKey, config]) => {
          const points = seriesByMetric[metricKey] || [];
          const currentValue = readActivityValue(product, metricKey);
          const Chart = config.chartType === 'line' ? MiniLineChart : MiniBarChart;

          return (
            <button
              key={metricKey}
              type="button"
              className="stats-activity-card"
              onClick={() => setSelectedMetric(metricKey)}
            >
              <div className="stats-activity-card__header">
                <span>{config.label}</span>
                <strong>{formatNumber(currentValue)}</strong>
              </div>
              <div className="stats-activity-card__chart">
                {loading ? (
                  <span className="stats-activity-card__loading">読込中...</span>
                ) : (
                  <Chart points={points} />
                )}
              </div>
              <span className="stats-activity-card__hint">詳細を表示</span>
            </button>
          );
        })}
      </div>

      {selectedMetric && (
        <StatisticsMetricDetailModal
          metricKey={selectedMetric}
          productKey={productKey}
          targetDateKey={targetDateKey}
          onClose={() => setSelectedMetric(null)}
        />
      )}
    </>
  );
};

export default StatisticsProductActivityCards;
